import { dayKey, dueThrough } from './dayBoundary';
import { schedule, type Grade } from './srs';
import { supabase } from './supabase';

// A student's flashcards. RLS (20261004_student_flashcards.sql) keeps each
// deck to its owner; teachers can read their students' decks and day counts.

export type Card = {
  id: string;
  student_id: string;
  classroom_id: string | null;
  assignment_id: string | null;
  kind: 'word' | 'phrase';
  term: string;
  translation: string;
  source_lang: string;
  target_lang: string;
  interval: number;
  ease_factor: number;
  review_count: number;
  next_review: string;
  reviews: number;
  lapses: number;
  last_reviewed_at: string | null;
  created_at: string;
};

export type ReviewDay = { student_id: string; day: string; reviewed: number; forgot: number };

function check<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export const normTerm = (s: string) => s.replace(/\s+/g, ' ').trim();
export const termKey = (s: string) => normTerm(s).toLowerCase();

export function isDue(c: Card, now: Date = new Date()): boolean {
  return new Date(c.next_review).getTime() <= dueThrough(now).getTime();
}

export async function fetchMyCards(): Promise<Card[]> {
  return check(await supabase.from('student_cards').select('*').order('created_at', { ascending: false }));
}

export async function saveCard(p: {
  term: string; translation: string; sourceLang: string; targetLang: string;
  classroomId?: string | null; assignmentId?: string | null;
}): Promise<Card> {
  const term = normTerm(p.term);
  const row = {
    term,
    translation: normTerm(p.translation),
    kind: /\s/.test(term) ? 'phrase' : 'word',
    source_lang: p.sourceLang,
    target_lang: p.targetLang,
    classroom_id: p.classroomId ?? null,
    assignment_id: p.assignmentId ?? null,
  };
  let res = await supabase.from('student_cards').insert(row).select().single();
  // 23503: the assignment (or class) the page was opened on has since been
  // deleted or replaced by the teacher. The link is only a note of where the
  // word came from, so the card is saved without it.
  if (res.error?.code === '23503') {
    res = await supabase.from('student_cards')
      .insert({ ...row, classroom_id: null, assignment_id: null }).select().single();
  }
  if (res.error?.code === '23505') throw new Error('Already in your flashcards.');
  return check(res);
}

export async function updateCardTranslation(id: string, translation: string): Promise<void> {
  check(await supabase.from('student_cards').update({ translation: normTerm(translation) }).eq('id', id));
}

export async function deleteCard(id: string): Promise<void> {
  check(await supabase.from('student_cards').delete().eq('id', id));
}

export async function reviewCard(card: Card, grade: Grade): Promise<Card> {
  const next = schedule(
    { interval: card.interval, easeFactor: card.ease_factor, reviewCount: card.review_count }, grade,
  );
  return check(await supabase.rpc('review_card', {
    p_card_id: card.id,
    p_good: grade === 'good',
    p_interval: next.interval,
    p_ease_factor: next.easeFactor,
    p_review_count: next.reviewCount,
    p_next_review: next.nextReview.toISOString(),
    p_day: dayKey(),
  }));
}

/** Teacher: every card and recent review day for these students. */
export async function fetchClassDecks(studentIds: string[]): Promise<{ cards: Card[]; days: ReviewDay[] }> {
  if (studentIds.length === 0) return { cards: [], days: [] };
  const since = new Date(Date.now() - 14 * 86_400_000).toLocaleDateString('en-CA');
  const [cards, days] = await Promise.all([
    supabase.from('student_cards').select('*').in('student_id', studentIds),
    supabase.from('card_review_days').select('*').in('student_id', studentIds).gte('day', since),
  ]);
  return { cards: check(cards), days: check(days) };
}
